import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import MountainTrailHero from "@/components/MountainTrailHero";
import { useAuth } from "@/context/AuthContext";
import { Compass, MapPin, Flag, Sparkles, ShieldCheck, Trophy } from "lucide-react";

const Landing = () => {
  return (
    <div className="min-h-screen bg-mm-parchment bg-topo">
      <Navbar />

      {/* HERO */}
      <section className="container mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 py-16 lg:py-24 items-center">
        <div className="fade-up">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-bold text-mm-steel mb-6">
            <Compass size={16} strokeWidth={2.5} /> K-12 Learning Expedition
          </div>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black text-mm-navy leading-[1.02] tracking-tight">
            Every concept is a <span className="text-mm-gold">summit</span> waiting to be climbed.
          </h1>
          <p className="mt-6 text-lg text-mm-navy/75 max-w-xl leading-relaxed">
            Mind Map turns any topic or state standard into a five-stage learning expedition — Base Camp,
            three trails, and the summit — guided by AI and powered by your child's curiosity.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-4">
            <Link to="/signup" className="btn-trail btn-gold text-base" data-testid="hero-start-expedition-btn">
              <Sparkles size={18} strokeWidth={2.5} /> Start your expedition
            </Link>
            <Link to="/pricing" className="btn-trail btn-outline-navy text-base" data-testid="hero-view-pricing-btn">
              Choose your trail
            </Link>
          </div>
          <div className="mt-8 flex items-center gap-6 text-sm text-mm-navy/60">
            <span className="flex items-center gap-2"><ShieldCheck size={16} strokeWidth={2.5} className="text-mm-steel" /> 14-day free trial</span>
            <span className="flex items-center gap-2"><Trophy size={16} strokeWidth={2.5} className="text-mm-gold" /> No credit card to start</span>
          </div>
        </div>

        <div className="fade-up" style={{ animationDelay: "120ms" }}>
          <div className="relative">
            <MountainTrailHero progressIndex={2} />
            <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl border border-mm-navy/10 px-5 py-3 shadow-lg hidden md:flex items-center gap-3" data-testid="hero-ap-badge">
              <div className="w-10 h-10 rounded-full bg-mm-gold/20 flex items-center justify-center">
                <Flag size={20} strokeWidth={2.5} className="text-mm-gold" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-mm-steel font-bold">Altitude Points</div>
                <div className="font-heading text-xl font-black text-mm-navy">+1,240 AP today</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURE STRIP */}
      <section className="container mx-auto py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <FeatureCard
            icon={<MapPin size={22} strokeWidth={2.5} />}
            title="Topic to expedition in seconds"
            body="Type any concept (long division) or paste a state standard code — AI builds the 5-stage path: Base Camp, Trails 1-3, and the Summit quiz."
            testid="feature-card-generator"
          />
          <FeatureCard
            icon={<Compass size={22} strokeWidth={2.5} />}
            title="Routes that fit every climber"
            body="A placement quiz auto-routes students to the Rescue Route for extra scaffolding or the Explorer's Challenge for deeper thinking."
            testid="feature-card-routing"
          />
          <FeatureCard
            icon={<Trophy size={22} strokeWidth={2.5} />}
            title="Climb. Earn. Celebrate."
            body="Altitude Points, badges like 'Summit Conqueror,' a Compass Streak, and a Mountain Progress Map make every step rewarding."
            testid="feature-card-gamification"
          />
        </div>
      </section>

      {/* PRICING SHORT */}
      <section id="pricing" className="container mx-auto py-16">
        <div className="text-center mb-10">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-mm-steel">Expedition Outfitter</div>
          <h2 className="font-heading text-4xl sm:text-5xl font-black text-mm-navy mt-3">Choose your trail</h2>
          <p className="text-mm-navy/70 mt-3 max-w-xl mx-auto">Start free. Upgrade when your climber is ready for unlimited expeditions and personalized routes — or bring the whole classroom aboard with anonymous student accounts.</p>
        </div>
        <PricingTwoCol />
      </section>

      <footer className="border-t border-mm-navy/10 py-10 mt-10">
        <div className="container mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-mm-navy/60">
          <div>© 2026 The Meadows Mindset. Plot your path. Climb your peak.</div>
          <div className="flex gap-6">
            <Link to="/pricing" className="hover:text-mm-navy">Pricing</Link>
            <Link to="/login" className="hover:text-mm-navy">Sign in</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

const FeatureCard = ({ icon, title, body, testid }) => (
  <div className="stage-card p-7" data-testid={testid}>
    <div className="w-12 h-12 rounded-xl bg-mm-steel/15 text-mm-navy flex items-center justify-center mb-5">
      {icon}
    </div>
    <h3 className="font-heading text-xl font-bold text-mm-navy">{title}</h3>
    <p className="text-mm-navy/70 mt-2 leading-relaxed text-[15px]">{body}</p>
  </div>
);

export const PricingTwoCol = () => {
  const { user } = useAuth();
  // Smart Summit Pass CTA destination:
  // - Logged in  → /pricing?action=checkout (auto-fires Stripe checkout there)
  // - Logged out → /signup?next=/pricing%3Faction%3Dcheckout (preserve intent through auth)
  const summitHref = user
    ? "/pricing?action=checkout"
    : "/signup?next=" + encodeURIComponent("/pricing?action=checkout");
  const freeHref = user ? "/dashboard" : "/signup";
  const teacherHref = user
    ? (user.role === "teacher" ? "/teacher/dashboard" : "/dashboard")
    : "/signup?role=teacher";

  return (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto items-stretch">
    <div className="stage-card p-8" data-testid="plan-free-card">
      <div className="text-xs uppercase tracking-[0.22em] font-bold text-mm-steel">Day Hike</div>
      <div className="mt-2 font-heading text-3xl font-black text-mm-navy">Free</div>
      <div className="text-mm-navy/60 mt-1">Get on the trail. No card needed.</div>
      <ul className="mt-6 space-y-3 text-mm-navy/80">
        <li>• 3 active expeditions at a time</li>
        <li>• Standard route only</li>
        <li>• Basic badges & altitude points</li>
        <li>• Compass streak tracking</li>
      </ul>
      <Link to={freeHref} className="btn-trail btn-outline-navy mt-7 w-full" data-testid="plan-free-cta">
        {user ? "Go to dashboard" : "Start free"}
      </Link>
    </div>

    <div className="stage-card p-8 relative border-2 border-mm-gold/60 bg-white" data-testid="plan-premium-card">
      <div className="absolute -top-3 right-6 bg-mm-gold text-mm-navy text-xs font-bold px-3 py-1 rounded-full uppercase tracking-widest" data-testid="plan-popular-badge">Most popular</div>
      <div className="text-xs uppercase tracking-[0.22em] font-bold text-mm-gold">Summit Pass</div>
      <div className="mt-2 flex items-baseline gap-2">
        <div className="font-heading text-4xl font-black text-mm-navy">$5</div>
        <div className="text-mm-navy/60">/ month</div>
      </div>
      <div className="text-mm-navy/60 mt-1">or <strong>$45 / school year</strong> (save $15)</div>
      <div className="text-xs uppercase tracking-widest text-mm-steel font-bold mt-3">14-day free trial</div>
      <ul className="mt-6 space-y-3 text-mm-navy/85">
        <li>• Unlimited expeditions</li>
        <li>• Rescue Route & Explorer's Challenge</li>
        <li>• Full badge collection & leaderboard</li>
        <li>• Detailed progress reports + printable guides</li>
      </ul>
      <Link to={summitHref} className="btn-trail btn-gold mt-7 w-full" data-testid="plan-premium-cta">
        {user ? "Lock in Summit Pass" : "Reach the summit"}
      </Link>
    </div>

    <div className="stage-card p-8 relative border-2 border-mm-navy/20 bg-white" data-testid="plan-teacher-card">
      <div className="absolute -top-3 right-6 bg-mm-navy text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-widest">For classrooms</div>
      <div className="text-xs uppercase tracking-[0.22em] font-bold text-mm-steel">Teacher Trail</div>
      <div className="mt-2 flex items-baseline gap-2">
        <div className="font-heading text-4xl font-black text-mm-navy">$100</div>
        <div className="text-mm-navy/60">/ school year</div>
      </div>
      <div className="text-mm-navy/60 mt-1">30 anonymous student accounts — zero PII.</div>
      <div className="text-xs uppercase tracking-widest text-mm-steel font-bold mt-3">Renews annually</div>
      <ul className="mt-6 space-y-3 text-mm-navy/85">
        <li>• 30 anonymous seats per class · no email, no name</li>
        <li>• Printable login cards & class roster (PDF)</li>
        <li>• Push a whole-class expedition in one click</li>
        <li>• Live monitor — see who's on trail in real time</li>
      </ul>
      <Link to={teacherHref} className="btn-trail btn-outline-navy mt-7 w-full" data-testid="plan-teacher-cta">
        {user
          ? (user.role === "teacher" ? "Go to my classes" : "Go to dashboard")
          : "Start teaching"}
      </Link>
      <Link to="/schools/purchase-order" className="mt-3 block text-center text-xs font-bold text-mm-navy underline hover:text-mm-steel" data-testid="plan-teacher-po-link">
        Purchasing for your school? Request a Purchase Order →
      </Link>
    </div>
  </div>
  );
};

export default Landing;
